import type { DayPayments } from "@vaulted/shared";
import { PAYMENT_METHOD_LABELS, formatCents } from "@vaulted/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { errorMessage, get } from "../api";
import { useSession } from "../session";
import { ExportButtons } from "../components/ExportButtons";
import { type ReportTable, dollars } from "../components/exportReport";
import { Alert, Badge, Button, Loading, Panel, PanelHeader } from "../components/ui";
import { addDays, todayIn } from "./Calendar";

/** Reports → Payments: the day's payments, totals by method, tips by nurse, and every payment (for closing out). */
export function PaymentsReport() {
  const { me } = useSession();
  const [date, setDate] = useState(() => todayIn(me?.clinic.timezone));
  const data = useQuery({ queryKey: ["payments-day", date], queryFn: () => get<DayPayments>(`/api/payments/day?date=${date}`) });
  const d = data.data;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="quiet" aria-label="Previous day" onClick={() => setDate(addDays(date, -1))}>
          ‹
        </Button>
        <input type="date" aria-label="Date" className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5 text-sm" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
        <Button variant="quiet" aria-label="Next day" onClick={() => setDate(addDays(date, 1))}>
          ›
        </Button>
        <span className="text-sm text-muted">{new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" })}</span>
        <span className="flex-1" />
        {d && <ExportButtons name={`Payments ${date}`} tables={() => paymentTables(d)} />}
      </div>
      {data.error && <Alert>{errorMessage(data.error)}</Alert>}
      {!d ? (
        !data.error && <Loading />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {d.totals.map((t) => (
              <Panel key={t.method} className="p-4">
                <p className="text-sm text-muted">{PAYMENT_METHOD_LABELS[t.method]}</p>
                <p className="text-2xl font-semibold tabular-nums">{formatCents(t.amountCents)}</p>
                <p className="text-xs text-muted">
                  {t.count} payment{t.count === 1 ? "" : "s"}
                  {t.tipCents > 0 && ` · ${formatCents(t.tipCents)} in tips`}
                </p>
              </Panel>
            ))}
            {d.refundsCents > 0 && (
              <Panel className="p-4">
                <p className="text-sm text-muted">Refunds</p>
                <p className="text-2xl font-semibold text-danger tabular-nums">−{formatCents(d.refundsCents)}</p>
              </Panel>
            )}
            {!d.totals.length && <p className="text-sm text-muted">No payments this day.</p>}
          </div>
          {d.tipsByNurse.length > 0 && (
            <Panel>
              <PanelHeader title="Tips by nurse" />
              <ul className="px-5 pb-3 text-sm">
                {d.tipsByNurse.map((t) => (
                  <li key={t.staffId} className="flex justify-between border-b border-mist py-2 last:border-0">
                    <span>{t.name}</span>
                    <span className="font-semibold tabular-nums">{formatCents(t.tipCents)}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
          {d.payments.length > 0 && (
            <Panel>
              <PanelHeader title="All payments" />
              <ul className="px-5 pb-3 text-sm">
                {d.payments.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist py-2 last:border-0">
                    <span className="w-20 text-muted tabular-nums">{new Date(p.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                    <span className="min-w-0 flex-1">
                      {p.visit ? (
                        <Link to={`/checkout/${p.visit.appointmentId}`} className="font-semibold hover:underline">
                          {p.visit.clientName}
                        </Link>
                      ) : (
                        <span className="font-semibold">{p.payer ?? "—"}</span>
                      )}
                      <span className="text-muted">
                        {" "}
                        · {p.kind === "refund" ? "Refund" : p.kind === "deposit" ? "Deposit" : p.kind === "fee" ? "Fee" : "Payment"} · {PAYMENT_METHOD_LABELS[p.method]}
                        {p.cardLast4 && ` •••• ${p.cardLast4}`}
                      </span>
                    </span>
                    {p.tipCents > 0 && <Badge tone="ok">Tip {formatCents(p.tipCents)}</Badge>}
                    <span className="w-24 text-right font-semibold tabular-nums">
                      {p.kind === "refund" ? "−" : ""}
                      {formatCents(p.amountCents)}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </>
      )}
    </div>
  );
}

const KIND_LABELS = { refund: "Refund", deposit: "Deposit", fee: "Fee", checkout: "Payment", plan: "Membership or package" } as const;

function paymentTables(d: DayPayments): ReportTable[] {
  return [
    {
      title: "Totals",
      columns: ["Method", "Payments", "Amount", "Tips"],
      rows: [...d.totals.map((t) => [PAYMENT_METHOD_LABELS[t.method], t.count, dollars(t.amountCents), dollars(t.tipCents)]), ["Refunds", "", dollars(-d.refundsCents), ""]],
    },
    { title: "Tips by nurse", columns: ["Nurse", "Tips"], rows: d.tipsByNurse.map((t) => [t.name, dollars(t.tipCents)]) },
    {
      title: "All payments",
      columns: ["Time", "Client", "Kind", "Method", "Card", "Amount", "Tip"],
      rows: d.payments.map((p) => [
        new Date(p.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
        p.visit?.clientName ?? p.payer ?? "",
        KIND_LABELS[p.kind],
        PAYMENT_METHOD_LABELS[p.method],
        p.cardLast4 ?? "",
        dollars(p.kind === "refund" ? -p.amountCents : p.amountCents),
        dollars(p.tipCents),
      ]),
    },
  ];
}
